import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight,
  ArrowLeft,
  Home,
  Sparkles,
  Calendar,
  ChevronRight,
  Lock,
  Clock,
  Armchair,
  Eye,
  BookOpen,
  MessageSquare,
  Unlock,
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin, EditableText } from '../AdminContentEditor';

const API_URL = window.location.origin;

// Check if email is admin
const checkIsAdmin = (email) => {
  const adminEmails = ['me@iamnaomithomas.com', 'naomi@datadreaming.cloud'];
  return adminEmails.includes(email?.toLowerCase());
};

// Default images for overview page that persist across deployments
const OVERVIEW_DEFAULT_IMAGES = {
  'dd-overview-build_feature-image': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/61zs405t_Front%20Row%20assets%20%281%29.png',
};

// Editable Image Component with Backend Persistence
const EditableImage = ({ sectionId, imageKey, caption, className = '', aspectRatio = 'aspect-video' }) => {
  const { isAdmin, isEditMode, sectionContent, updateSectionContent } = useAdmin();
  const [localImage, setLocalImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  
  const storageKey = `dd_overview_image_${sectionId}_${imageKey}`;
  const backendKey = `${sectionId}_${imageKey}`;
  const defaultImage = OVERVIEW_DEFAULT_IMAGES[backendKey] || null;
  
  // Load image from backend content first, then localStorage as fallback, then default
  useEffect(() => {
    // Check backend content first
    const backendData = sectionContent[sectionId];
    if (backendData?.custom_fields?.[imageKey] || backendData?.[imageKey]) {
      const savedUrl = backendData.custom_fields?.[imageKey] || backendData[imageKey];
      // Skip broken /api/uploads/ URLs
      if (savedUrl && !savedUrl.includes('/api/uploads/')) {
        setLocalImage(savedUrl);
        return;
      }
    }
    
    // Fallback to localStorage
    const saved = localStorage.getItem(storageKey);
    if (saved && !saved.includes('/api/uploads/')) {
      setLocalImage(saved);
      return;
    }
    
    // Fallback to default image
    if (defaultImage) {
      setLocalImage(defaultImage);
    }
  }, [storageKey, sectionContent, sectionId, imageKey, defaultImage]);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get auth token
      const sessionToken = localStorage.getItem('auth_session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/upload`, { 
        method: 'POST', 
        headers,
        credentials: 'include',
        body: formData 
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url || data.file_url;
        const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`;
        
        // Save to backend
        await updateSectionContent(sectionId, { [imageKey]: fullUrl });
        localStorage.setItem(storageKey, fullUrl);
        setLocalImage(fullUrl);
        console.log(`[EditableImage] Saved to backend: ${backendKey}`);
        setUploading(false);
        return;
      }
    } catch (error) {
      console.log('Backend upload failed, using localStorage fallback:', error);
    }
    
    // Fallback to localStorage with data URL
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result;
      localStorage.setItem(storageKey, dataUrl);
      // Also try to save to backend
      await updateSectionContent(sectionId, { [imageKey]: dataUrl });
      setLocalImage(dataUrl);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const clearImage = async () => {
    localStorage.removeItem(storageKey);
    await updateSectionContent(sectionId, { [imageKey]: null });
    setLocalImage(null);
  };
  
  if (localImage) {
    return (
      <>
        <div 
          className={`relative rounded-2xl overflow-hidden bg-gray-100 ${className} ${!isEditMode ? 'cursor-zoom-in group' : ''}`}
          onClick={() => !isEditMode && setShowLightbox(true)}
        >
          {/* Use object-contain to prevent cropping */}
          <img src={localImage} alt={caption} className="w-full h-auto object-contain" />
          {!isEditMode && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                <ArrowRight className="w-5 h-5 text-gray-700 rotate-[-45deg]" />
              </div>
            </div>
          )}
          {isAdmin && isEditMode && (
            <button onClick={clearImage} className="absolute top-2 right-2 p-2 rounded-full bg-red-500 text-white hover:bg-red-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Lightbox */}
        {showLightbox && (
          <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
            <button onClick={() => setShowLightbox(false)} className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white">
              <X className="w-6 h-6" />
            </button>
            <img src={localImage} alt={caption} className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </>
    );
  }
  
  return (
    <div className={`bg-gray-100 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center min-h-[200px] ${className}`}>
      {isAdmin && isEditMode ? (
        <label className="cursor-pointer flex flex-col items-center p-6 w-full h-full justify-center hover:bg-gray-200 transition-colors rounded-2xl">
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          {uploading ? (
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-purple-500 mb-2" />
              <span className="text-sm text-purple-600 font-medium">Upload Image</span>
              <span className="text-xs text-gray-600 mt-1 text-center">{caption}</span>
            </>
          )}
        </label>
      ) : (
        <>
          <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
          <span className="text-sm text-gray-600 text-center font-medium">{caption}</span>
        </>
      )}
    </div>
  );
};

// Scroll to top on navigation
const ScrollToTopLink = ({ to, children, className, ...props }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    navigate(to);
  };
  
  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

// Access Gate Component
const AccessGate = ({ onRequestAccess }) => {
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setChecking(true);
    localStorage.setItem('datadreaming_frontrow_approved', 'true');
    localStorage.setItem('datadreaming_frontrow_email', email);
    onRequestAccess();
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-md text-center"
      >
        <div className="w-24 h-24 rounded-2xl bg-black flex items-center justify-center mx-auto mb-6">
          <Armchair className="w-16 h-16 text-white" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Front Row</h1>
        <p className="text-gray-500 mb-8 text-balance">
          A front row seat to building a digital community platform
        </p>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-purple-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Required</h2>
          <p className="text-gray-600 text-sm mb-6 text-balance">
            Enter your email to access the Front Row documentation series.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-center text-gray-900"
              data-testid="email-input"
            />
            <button
              type="submit"
              disabled={checking}
              className="w-full py-3 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
              data-testid="access-submit-btn"
            >
              {checking ? 'Checking...' : 'Get Access'}
            </button>
          </form>
        </div>

        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 text-sm mt-8 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" />
          Back to Data Dreaming
        </Link>
      </motion.div>
    </div>
  );
};

// Main Component
const DataDreamingFrontRow = () => {
  // No access gate - page is published but unlisted
  const hasAccess = true;
  const { isAdmin } = useAdmin();

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Phygital Style */}
      <header className="border-b border-gray-100 sticky top-0 bg-white z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                <Armchair className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Front Row</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Powered by</span>
            <span className="font-semibold text-gray-900">Phygital</span>
          </div>
        </div>
      </header>

      {/* Hero - Phygital Style */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Large Seat Icon */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-20 h-20 rounded-2xl bg-black flex items-center justify-center">
                <Armchair className="w-14 h-14 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900">
                <EditableText
                  sectionId="dd-overview-hero"
                  field="title"
                  defaultValue="Front Row"
                  className="text-4xl font-bold text-gray-900"
                />
              </h1>
            </div>

            {/* Pink Badge */}
            <p className="text-sm font-medium text-pink-600 uppercase tracking-wider mb-6">
              <EditableText
                sectionId="dd-overview-hero"
                field="badge"
                defaultValue="For Builders and Digital Creatives"
                className="text-sm font-medium text-pink-600 uppercase tracking-wider"
              />
            </p>
            
            {/* Main Heading */}
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight text-balance">
              <EditableText
                sectionId="dd-overview-hero"
                field="heading"
                defaultValue="Get a Front Row Seat to Building a Digital Community Platform"
                className="text-4xl md:text-5xl font-bold text-gray-900"
              />
            </h2>
            
            {/* Subtitles */}
            <p className="text-xl text-gray-600 mb-4 max-w-2xl mx-auto text-balance">
              <EditableText
                sectionId="dd-overview-hero"
                field="subtitle1"
                defaultValue="A front row seat to building a digital community platform."
                className="text-xl text-gray-600"
              />
            </p>
            
            <p className="text-gray-500 mb-8 max-w-xl mx-auto text-balance">
              <EditableText
                sectionId="dd-overview-hero"
                field="subtitle2"
                defaultValue="Front Row documents the creation of the Data Dreaming ecosystem from the earliest whiteboard ideas to a working platform."
                className="text-gray-500"
              />
            </p>

            {/* Month 1 Button */}
            <div className="space-y-3">
              <ScrollToTopLink
                to="/lab/frontrow/datadreaming/month-1"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors"
                data-testid="month1-btn"
              >
                <Calendar className="w-6 h-6" />
                Month 1
                <ArrowRight className="w-5 h-5" />
              </ScrollToTopLink>
              <p className="text-gray-400 text-sm">
                <EditableText
                  sectionId="dd-overview-hero"
                  field="button-caption"
                  defaultValue="Start with the first workshop"
                  className="text-gray-400 text-sm"
                />
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Data Dreaming Build - With Image */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              <EditableText
                sectionId="dd-overview-build"
                field="title"
                defaultValue="The Data Dreaming Build"
                className="text-2xl font-bold text-gray-900"
              />
            </h2>
            
            <div className="max-w-xl mx-auto mb-8">
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/61zs405t_Front%20Row%20assets%20%281%29.png"
                  alt="Data Dreaming Build"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <p className="text-gray-700 mb-4 text-balance">
                <EditableText
                  sectionId="dd-overview-build"
                  field="para1"
                  defaultValue="Data Dreaming is a community platform for creative and analytical builders."
                  className="text-gray-700"
                />
              </p>
              <p className="text-gray-600 mb-4 text-balance">
                <EditableText
                  sectionId="dd-overview-build"
                  field="para2"
                  defaultValue="Front Row: Data Dreaming documents the process of building that platform from the first whiteboard session through the early stages of the community."
                  className="text-gray-600"
                />
              </p>
              <p className="text-gray-600 mb-4 text-balance">
                <EditableText
                  sectionId="dd-overview-build"
                  field="para3"
                  defaultValue="Each month captures a different stage of the build."
                  className="text-gray-600"
                />
              </p>
              <p className="text-gray-700 font-medium text-balance">
                <EditableText
                  sectionId="dd-overview-build"
                  field="para4"
                  defaultValue="Month 1 focuses on the foundation of the platform."
                  className="text-gray-700 font-medium"
                />
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Front Row Is */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              <EditableText
                sectionId="dd-overview-whatIs"
                field="title"
                defaultValue="What Front Row Is"
                className="text-2xl font-bold text-gray-900"
              />
            </h2>
            <p className="text-gray-700 mb-4 text-balance">
              <EditableText
                sectionId="dd-overview-whatIs"
                field="intro1"
                defaultValue="Front Row is a documentation format for builders."
                className="text-gray-700"
              />
            </p>
            <p className="text-gray-600 mb-8 text-balance">
              <EditableText
                sectionId="dd-overview-whatIs"
                field="intro2"
                defaultValue="It captures the thinking behind building a platform instead of only showing the final result."
                className="text-gray-600"
              />
            </p>
            
            <p className="text-gray-700 mb-4">
              <EditableText
                sectionId="dd-overview-whatIs"
                field="listIntro"
                defaultValue="Each stage of the build explores a different part of the process including:"
                className="text-gray-700"
              />
            </p>
            <ul className="space-y-3 mb-8 inline-block text-left">
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  <EditableText sectionId="dd-overview-whatIs" field="bullet1" defaultValue="defining the goals of the system" className="text-gray-700" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  <EditableText sectionId="dd-overview-whatIs" field="bullet2" defaultValue="mapping the structure of the platform" className="text-gray-700" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  <EditableText sectionId="dd-overview-whatIs" field="bullet3" defaultValue="building the experience" className="text-gray-700" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  <EditableText sectionId="dd-overview-whatIs" field="bullet4" defaultValue="testing the system in real environments" className="text-gray-700" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  <EditableText sectionId="dd-overview-whatIs" field="bullet5" defaultValue="sharing reusable prompts and resources" className="text-gray-700" />
                </span>
              </li>
            </ul>
            
            <p className="text-gray-700 font-medium text-balance mb-10">
              <EditableText
                sectionId="dd-overview-whatIs"
                field="conclusion"
                defaultValue="The goal is to make the building process visible."
                className="text-gray-700 font-medium"
              />
            </p>
            
            {/* The Front Row Framework - Below bullets */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <EditableText
                  sectionId="dd-overview-framework"
                  field="title"
                  defaultValue="The Front Row Framework"
                  className="text-xl font-bold text-gray-900"
                />
              </h3>
              <p className="text-gray-600 mb-6">
                <EditableText
                  sectionId="dd-overview-framework"
                  field="description"
                  defaultValue="Front Row is a simple framework I use to show how ideas move from concept to working system."
                  className="text-gray-600"
                />
              </p>
              
              {/* Framework Flow - Single Line */}
              <div className="flex items-center justify-center gap-1 md:gap-2 text-xs md:text-sm">
                <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-lg bg-pink-100 text-pink-700 font-semibold">
                  IDEA
                </div>
                <span className="text-gray-400">→</span>
                <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-lg bg-purple-100 text-purple-700 font-semibold">
                  WHITEBOARD
                </div>
                <span className="text-gray-400">→</span>
                <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-lg bg-blue-100 text-blue-700 font-semibold">
                  BUILD
                </div>
                <span className="text-gray-400">→</span>
                <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-lg bg-orange-100 text-orange-700 font-semibold">
                  TEST IRL
                </div>
                <span className="text-gray-400">→</span>
                <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-lg bg-teal-100 text-teal-700 font-semibold">
                  SHARE
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet Your Instructor */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                <EditableText
                  sectionId="dd-overview-instructor"
                  field="title"
                  defaultValue="Meet Your Instructor"
                  className="text-2xl font-bold text-gray-900"
                />
              </h2>
            </div>
            
            <div className="flex flex-col items-center gap-8">
              {/* Photo */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-pink-200 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg"
                    alt="Naomi Thomas"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Bio */}
              <div className="flex-1 text-center max-w-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <EditableText
                    sectionId="dd-overview-instructor"
                    field="name"
                    defaultValue="Naomi Thomas"
                    className="text-xl font-bold text-gray-900"
                  />
                </h3>
                <p className="text-pink-600 font-medium mb-4">
                  <EditableText
                    sectionId="dd-overview-instructor"
                    field="role"
                    defaultValue="Phygital Strategist & Builder"
                    className="text-pink-600 font-medium"
                  />
                </p>
                <p className="text-gray-600 mb-4 text-balance">
                  <EditableText
                    sectionId="dd-overview-instructor"
                    field="bio1"
                    defaultValue="Naomi builds at the intersection of physical and digital experiences. She creates systems that help people understand how technology and creativity work together."
                    className="text-gray-600"
                  />
                </p>
                <p className="text-gray-600 text-balance">
                  <EditableText
                    sectionId="dd-overview-instructor"
                    field="bio2"
                    defaultValue="Through Front Row, she documents the real process of building platforms, sharing not just the polished results, but the thinking, decisions, and iterations along the way."
                    className="text-gray-600"
                  />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Start with Month 1 CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-pink-600 to-purple-600">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              <EditableText
                sectionId="dd-overview-cta"
                field="title"
                defaultValue="Start with Month 1"
                className="text-2xl font-bold text-white"
              />
            </h2>
            <p className="text-white/90 mb-4 text-balance">
              <EditableText
                sectionId="dd-overview-cta"
                field="subtitle"
                defaultValue="Front Row: Data Dreaming Month 1"
                className="text-white/90"
              />
            </p>
            <p className="text-white/80 mb-8 max-w-xl mx-auto text-balance">
              <EditableText
                sectionId="dd-overview-cta"
                field="description"
                defaultValue="Month 1 explores how the Data Dreaming platform evolved from the whiteboard stage to a working system and live community workshop."
                className="text-white/80"
              />
            </p>
            <ScrollToTopLink
              to="/lab/frontrow/datadreaming/month-1"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-pink-600 font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="explore-month1-btn"
            >
              Explore Month 1
              <ArrowRight className="w-5 h-5" />
            </ScrollToTopLink>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <Link to="/" className="text-gray-500 text-sm hover:text-gray-600">
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default DataDreamingFrontRow;
